<template>
  <v-container>
    <v-breadcrumbs
      v-if="!!property"
      :items="breadCrumbs"
      divider="/"
      class="pl-0 mx-4"
    />
    <v-row>
      <v-col class="py-0">
        <h1 class="text-h4 font-weight-light mb-0 mt-0 ml-2">
          Sähköinen {{ isRentalLot ? 'vuokraoikeuden kauppa' : 'kiinteistökauppa' }}
        </h1>
      </v-col>
      <v-btn
        icon
        class="mr-4"
        color="primary"
        @click="showDIASHelp = true"
      >
        <v-icon>help_outline</v-icon>
      </v-btn>
    </v-row>

    <v-card
      v-if="archived"
      class="mt-6 pa-6 helmi-card"
    >
      <v-card-title class="text-h4 font-weight-light">
        DIAS on arkistoinut tämän kaupan tiedot
      </v-card-title>
      <v-card-text>
        <p>
          Otathan yhteyttä
          <b>DIASin asiakaspalveluun</b> jos tarvitset apua kaupan tietojen kanssa:
          Puh. +358 10 201 1190 (puhelun hinta pvm / mpm), sähköposti: asiakaspalvelu@dias.fi
        </p>
      </v-card-text>
    </v-card>

    <trade-attachments
      v-if="tradeId && archived"
      :trade-id="tradeId"
      class="mb-6"
    />
    <v-row v-if="!archived">
      <v-col md="7" cols="12">
        <dias-real-estate-status
          v-if="body.events"
          :body="body"
        />

        <dias-real-estate-documents
          :locked="locked"
          :body="body"
        />

        <dias-real-estate-accordion
          v-if="!locked"
          :body="body"
          :config="config"
          :banks="banks"
          :remove-pledges="removePledges"
          :realtor-payment-total="realtorPaymentTotal"
        />
        <dias-real-estate-events :body="body"/>
      </v-col>
      <v-col md="5" cols="12">
        <v-card outlined class="helmi-card">
          <v-card-text>
            <div class="helmi-card-title">
              Kaupan kohde
            </div>
            <p v-if="isRentalLot" class="mb-0 mt-0">
              Laitostunnus:
              <span v-if="property && property.apartment" class="primary--text">
                {{ body.laitostunnus }}</span>
            </p>
            <p v-else class="mb-0 mt-0">
              Kiinteistötunnus:
              <span v-if="property && property.apartment" class="primary--text">
                {{ body.kiinteistotunnus }}</span>
            </p>
            <p v-if="!!property" class="mb-0">
              Kiinteistön osoite:
              <span class="primary--text">
                {{ property.apartment.address.streetAddress }}
                {{ property.apartment.address.postalCode }}
                {{ property.apartment.address.city }}
              </span>
            </p>
            <p v-if="body.kauppahinta" class="mb-0">
              Kauppahinta: <span class="primary--text">
                {{ body.kauppahinta.amountInCents / 100 | currency }}
              </span>
            </p>
          </v-card-text>
        </v-card>

        <dias-real-estate-agent :body="body" :locked="locked"/>

        <dias-real-estate-realtor-payment
          :total="realtorPaymentTotal"
          :reference="realtorPaymentReferenceNumber"
          :property="property"
        />

        <dias-real-estate-actions
          :body="body"
          :phases="phases"
          :locked="locked"
          :remove-pledges="removePledges"
          :sendable="sendable"
        />
      </v-col>
    </v-row>

    <v-overlay
      :value="connectionFailure"
      color="white"
      opacity="0.95"
    >
      <v-col class="text-center">
        <v-icon color="primary lighten-2" size="180">
          error
        </v-icon>
        <p class="mb-6 mt-2 black--text text-center">
          DIAS ei ole tilapäisesti käytettävissä.<br>Yritäthän myöhemmin uudelleen.
        </p>
        <v-btn
          class="px-4"
          color="primary"
          rounded
          outlined
          @click="goToStart()"
        >
          <v-icon class="mr-1">
            keyboard_backspace
          </v-icon>
          Takaisin
        </v-btn>
      </v-col>
    </v-overlay>

    <v-dialog v-model="showDIASHelp" max-width="500">
      <v-card>
        <v-card-title>Tarvitsetko apua?</v-card-title>
        <v-card-text>
          Apua DIAS-kaupan mahdollisten ongelmatilanteiden ratkaisemiseen löydät
          <a href="https://asiakaspalvelu.dias.fi/hc/fi" target="_blank">
            DIASin asiakaspalvelun tukiportaalista
          </a>.
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn
            depressed
            color="primary"
            @click="showDIASHelp = false"
          >
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import body from './body';
import { apiGet, apiPost } from '../../api';

export default {
  components: {
    'dias-real-estate-accordion': () => import('@/components/DIAS/DIASRealEstateAccordion'),
    'dias-real-estate-status': () => import('@/components/DIAS/DIASRealEstateStatus'),
    'dias-real-estate-agent': () => import('@/components/DIAS/DIASRealEstateAgent'),
    'dias-real-estate-documents': () => import('@/components/DIAS/DIASRealEstateDocuments'),
    'dias-real-estate-realtor-payment': () => import('@/components/DIAS/DIASRealEstateRealtorPayment'),
    'dias-real-estate-actions': () => import('@/components/DIAS/DIASRealEstateActions'),
    'dias-real-estate-events': () => import('@/components/DIAS/DIASRealEstateEvents'),
    'trade-attachments': () => import('@/components/TradeAttachments.vue'),
  },
  data: () => ({
    tradeId: null,
    archived: false,
    banks: [],
    panel: null,
    body,
    originalBody: { ...body },
    config: {},
    removePledges: false,
    phases: [],
    locked: false,
    sendable: false,
    realtorPaymentTotal: 0,
    showDIASHelp: false,
    realtorPaymentReferenceNumber: '',

    suostujanTyyppi: {},
    property: null,
    connectionFailure: false,
  }),
  computed: {
    ...mapState({
      currentProperty: state => state.currentProperty,
    }),
    isRentalLot() {
      return this.currentProperty.tontinomistus === '1';
    },
    breadCrumbs() {
      if (!this.property) return [];
      return [
        {
          disabled: false,
          href: this.property.propertypath,
          link: true,
          text: this.property.apartment.address.streetAddress,
        }, {
          disabled: false,
          link: false,
          text: 'Sähköinen kaupankäynti',
        }];
    },
  },

  async mounted() {
    this.$bus.$emit('SHOW_GLOBAL_LOADER', 'Haetaan tietoja DIASista');
    await this.setCurrentProperty(this.$route.params.id);

    if (this.currentProperty.osakevaikiinteisto === '1') {
      this.$router.push({ name: 'dias', companyId: this.$route.params.companyId, id: this.$route.params.id });
      return;
    }

    await this.getBanks();
    await this.getTradeData();
    this.$bus.$emit('HIDE_GLOBAL_LOADER');

    this.$bus.$on('REMOVE_PLEDGES', () => {
      // delete this.body.panttikirjojenKoodit;
      this.removePledges = true;
    });

    this.$bus.$on('RESTORE_PLEDGES', () => {
      this.removePledges = false;
      // this.body.panttikirjojenKoodit = [];
    });

    this.$bus.$on('GET_DIAS_CONFIG', () => {
      this.getConfig();
    });

    this.$bus.$on('REFRESH_SALE_DATA', async () => {
      this.$bus.$emit('SHOW_GLOBAL_LOADER', 'Haetaan tietoja DIASista');
      await this.getTradeData();
      this.$bus.$emit('HIDE_GLOBAL_LOADER');
    });
    this.$bus.$on('EMPTY_SALE_DATA', async () => {
      this.$router.go(0);
    });
  },
  methods: {
    ...mapActions({
      setCurrentProperty: 'setCurrentProperty',
    }),
    goToStart() {
      this.$router.go(-1);
    },

    getPersonBank(personId) {
      return localStorage.getItem(`person-bank-${personId}`) || '';
    },

    getStoredBankSelections() {
      this.body.sellers.forEach((seller) => {
        if (seller.roles?.includes('ORGANIZATION_SIGNER') && !seller?.roles.includes('SIGNER')) {
          return;
        }

        seller.bankBusinessId = this.getPersonBank(seller.id); // eslint-disable-line
      });

      this.body.buyers.forEach((buyer) => {
        if (buyer.roles?.includes('ORGANIZATION_SIGNER') && !buyer.roles?.includes('SIGNER')) {
          return;
        }

        buyer.bankBusinessId = this.getPersonBank(buyer.id); // eslint-disable-line
      });
    },

    getStoredConditions() {
      const storedConditions = localStorage.getItem(`dias-conditions-${this.$route.params.id}`);

      if (storedConditions) {
        const conditions = JSON.parse(storedConditions);

        this.body.luovutuksenEhdot.push(...conditions);
      }
    },

    async getBanks() {
      const banksResponse = await apiGet('dias/banks');
      if (!banksResponse.result) {
        this.connectionFailure = true;
      } else {
        this.banks = banksResponse.result.data.values
          .map(b => ({ ...b, combName: `${b.name} (${b.groupName})` }));
      }
    },

    async getTradeData() {
      const dataExists = await this.getRealEstateSaleDraft();

      if (!dataExists) {
        await this.getPledges();
        await this.getPhases();
        await this.getSaleDetails();
        this.getStoredConditions();
        this.getStoredBankSelections();
        await this.getConfig();
      } else {
        this.sendable = true;
        await this.getPledges();
        await this.getConfig(false);
        await this.getPhases();
        await this.getSaleDetails(false);
      }
    },

    async getRealEstateSaleDraft() {
      let response = await apiGet(`dias/sale-draft/${this.$route.params.id}`);

      if (response?.result?.data?.status === 'INVALID_INPUT') {
        response = await apiGet(`dias/sale/${this.$route.params.id}`);
        this.locked = true;
      }
      if (response.success) {
        this.body = response?.result?.data;
        this.tradeId = response?.tradeId;

        if (!response.result.data.panttikirjojenKoodit) {
          this.removePledges = true;
        }

        if (response?.result?.data?.status === 'ARCHIVED') {
          this.archived = true;
        }
      }

      return response.success;
    },

    async getConfig(updateBody = true) {
      const sellerBankBusinessId = this.body?.sellers?.[0].bankBusinessId || '';
      const buyerBankBusinessId = this.body?.buyers?.[0].bankBusinessId || '';

      const payload = {
        buyersMortgageStatus: this.body.buyersMortgageStatus,
        eiSiirrettavatPanttikirjojenKoodit: this.body.eiSiirrettavatPanttikirjojenKoodit,
        panttikirjojenKoodit: this.body.panttikirjojenKoodit,
        sellerBankBusinessId,
        buyerBankBusinessId,
        sopimussakkoPurkamisestaJosKauppahintaaEiMaksetaErapaivana: this.body
          .sopimussakkoPurkamisestaJosKauppahintaaEiMaksetaErapaivana,
      };

      if (this.isRentalLot) {
        payload.laitostunnus = this.body.laitostunnus;
      }

      if (this.removePledges) {
        delete payload.panttikirjojenKoodit;
      }

      const response = await apiPost(`dias/config/${this.$route.params.id}`, payload);
      if (response.success) {
        if (!this.config.data) {
          this.config = response.result;
        } else {
          this.config.data.luovutuskirjanPakollisetEhdot = response
            .result.data.luovutuskirjanPakollisetEhdot;
        }
        if (updateBody) {
          this.body.luovutuksenEhdot = [
            ...response.result.data.luovutuskirjanPakollisetEhdot,
          ];
        }
      }
    },

    convertRealEstateCode(code) {
      if (code.includes('-')) {
        const parts = code.trim().split('-');

        try {
          if (parts.length === 5) {
            const lastPart = parts[4].slice(1);

            return `${parts[0].padStart(3, '0')}${parts[1].padStart(3, '0')}${parts[2].padStart(4, '0')}${parts[3].padStart(4, '0')}L${lastPart.padStart(4, '0')}`;
          }
          return `${parts[0].padStart(3, '0')}${parts[1].padStart(3, '0')}${parts[2].padStart(4, '0')}${parts[3].padStart(4, '0')}`;
        } catch (error) {
          return code.replace(/\D/g, '');
        }
      } else return code.replace(/\D/g, '');
    },

    async getPhases() {
      const response = await apiGet('dias/phases');
      if (response.success) {
        this.phases = response.result.data.phases;
      }
    },

    async getPledges() {
      const response = await apiGet(`dias/pledges/${this.$route.params.id}`);
      if (response.success) {
        this.body.panttikirjojenKoodit = response.result.map(item => item.identifier);
      }
    },

    setLuovutettavatOsuudet() {
      const organizationSellers = this.property.sellers
        .filter(seller => seller.roles.some(role => role === 'ORGANIZATION'));

      const personSellers = this.property.sellers
        .filter(seller => seller.roles.some(role => role === 'SIGNER'));

      this.body.luovutettavatOsuudet = [
        ...organizationSellers.map(o => ({
          osuus: `${o.share.dividend || 0}/${o.share.divider || 0}`,
          sellerSocialSecurityNumbers: [],
          sellerBusinessIds: [o.organization.businessId],
        })),
        ...personSellers.map(p => ({
          osuus: `${p.share.dividend || 0}/${p.share.divider || 0}`,
          sellerSocialSecurityNumbers: [p.person.socialSecurityNumber],
          sellerBusinessIds: [],
        }))];
    },

    async getSaleDetails(updateBody = true) {
      const response = await apiGet(`dias/confirmationsummary/${this.$route.params.id}`);
      this.property = response.result;

      if (updateBody) {
        this.body.buyers = this.property.buyers.map((buyer) => {
          let item = {
            roles: buyer.roles,
            id: buyer.id,
          };

          if (item.roles.includes('ORGANIZATION')) {
            item.organization = {
              name: buyer.organization.name,
              businessId: buyer.organization.businessId,
            };
          } else {
            item.person = {
              firstName: buyer.person.firstName,
              lastName: buyer.person.lastName,
              socialSecurityNumber: buyer.person.socialSecurityNumber,
              email: buyer.person.email,
            };
          }

          if (buyer.roles.includes('ORGANIZATION') || buyer.roles.includes('SIGNER')) {
            item = {
              ...item,
              bankBusinessId: buyer.person.bankBusinessId,
              osuus: `${buyer.person.share || 0}/${buyer.person.divider || 0}`,
              kenenLukuunLainhuutoaHaetaan: 'OL',
            };
          }

          return item;
        });

        this.body.realtorBankAccount.IBAN = this.property.companyBankAccounts?.[0].iban;
        this.body.realtorBankAccount.BIC = this.property.companyBankAccounts?.[0].bic;
        this.body.realtorBankAccount.name = this.property.companyBankAccounts?.[0].name;
      }

      const actualSellers = this.property.sellers.filter(seller => seller.roles.includes('SIGNER') || seller.roles.includes('ORGANIZATION'));

      this.realtorPaymentTotal = actualSellers?.[0].realtorPayment.amountInCents;
      this.realtorPaymentReferenceNumber = actualSellers?.[0].realtorPayment.referenceNumber;

      if (updateBody) {
        this.body.sellers = this.property.sellers.map((seller) => {
          const item = {
            roles: seller.roles,
            id: seller.person.id,
          };

          if (seller.roles.includes('ORGANIZATION')) {
            item.organization = {
              name: seller.organization.name,
              businessId: seller.organization.businessId,
            };
          } else {
            item.person = {
              firstName: seller.person.firstName,
              lastName: seller.person.lastName,
              socialSecurityNumber: seller.person.socialSecurityNumber,
              email: seller.person.email,
            };
          }

          if (seller.roles.includes('ORGANIZATION') || seller.roles.includes('SIGNER')) {
            item.bankBusinessId = seller.person.bankBusinessId;
          }

          if (seller.roles.includes('ORGANIZATION_SIGNER')) {
            item.businessIds = seller.businessIds;
          }

          if (seller.realtorPayment) {
            item.realtorPayment = {
              amountInCents: seller.realtorPayment.amountInCents / actualSellers.length,
              referenceNumber: seller.realtorPayment.referenceNumber,
            };
          }

          return item;
        });

        const possibleReceivers = this.body.sellers.filter(seller => seller.roles.includes('SIGNER') || seller.roles.includes('ORGANIZATION'));

        if (possibleReceivers.length > 0 && possibleReceivers[0]?.roles) {
          possibleReceivers[0].roles.push('LUONNOKSEN_VASTAANOTTAJA');
        }

        this.body.suostujat = this.property.sellers
          .filter(seller => seller.roles.length === 0).map(seller => ({
            person: {
              firstName: seller.person.firstName,
              lastName: seller.person.lastName,
              socialSecurityNumber: seller.person.socialSecurityNumber,
              email: seller.person.email,
            },
            suostujanTyyppi: null,
            sellerSocialSecurityNumber: null,
          }));

        this.setLuovutettavatOsuudet();

        if (this.isRentalLot) {
          delete this.body.kiinteistotunnus;
          delete this.body.luovutusilmoitus;

          this.body.laitostunnus = this
            .convertRealEstateCode(this.property.apartment.address.kiinteistotunnus);
        } else {
          delete this.body.laitostunnus;
          delete this.body.eiSiirrettavatPanttikirjojenKoodit;

          this.body.kiinteistotunnus = this
            .convertRealEstateCode(this.property.apartment.address.kiinteistotunnus);
        }

        const totalPrice = this.property.buyers
          .find(b => b.roles.includes('SIGNER') || b.roles.includes('ORGANIZATION'))?.person?.totalprice;

        this.body.kauppahinta.amountInCents = totalPrice;
      }
    },
  },
};
</script>
